import { useMemo } from 'react';
import { Scan } from '../../../../../services/entities/impl/blfApi/schemas/scan';
import { JsonLabel } from '../../../components/DebugInfoSection';

/**
 * Hook that creates memoized label objects for Scan data
 *
 * @param scans - Array of scan objects
 * @returns Memoized array of JsonLabel objects with id and formatted label
 */
export const useJsonLabelsScans = (scans: Scan[] | undefined): JsonLabel[] => {
  return useMemo(() => {
    if (!scans || !scans.length) {
      return [];
    }

    // Map scans to JsonLabel objects
    return scans
      .filter((scan) => !!scan.id) // Filter out items without ID
      .map((scan) => ({
        id: scan.id,
        label: formatScanLabel(scan),
      }));
  }, [scans]);
};

/**
 * Formats a scan label
 */
const formatScanLabel = (scan: Scan): string => {
  const mainParts = [];

  // Basic scan info
  if (scan.scanDate) {
    mainParts.push(scan.scanDate);
  }

  if (scan.customerShortCode) {
    mainParts.push(scan.customerShortCode);
  }

  if (scan.blockName) {
    mainParts.push(scan.blockName);
  }

  // Status indicators in brackets
  const flags = [];

  // Add pipeline status
  if (scan.pipelineStatus) {
    flags.push(scan.pipelineStatus);
  }

  // Add junk indicator
  if (scan.junk) {
    flags.push('Junk');
  }

  // Create the main part of the label with / separators
  let label = mainParts.length > 0 ? mainParts.join(' / ') : scan.name || '';

  // Add the flags section without a separator
  if (flags.length > 0) {
    label += ` [${flags.join(', ')}]`;
  }

  return label;
};
